import React, { useEffect, useMemo, useState } from 'react';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { Title } from '~/components/Title';
import { CustomAutocomplete } from '~/components/CustomAutocomplete';
import { useToast } from '~/services/hooks/toast';
import { useAssociations } from '~/services/hooks/useAssociations';
import { useAllocationContractors } from '~/services/hooks/useAllocationContractors';
import { useCustomer } from '~/services/hooks/useCustomer';
import { useCustomerVehicles } from '~/services/hooks/useCustomerVehicles';
import { useServices } from '~/services/hooks/useServices';
import { useNewOrder } from '~/services/hooks/useNewOrder';
import {
  allocateOpportunityForContractor,
  loadOpportunities,
  sendToManualAllocationAPI,
} from '~/services/api';
import {
  AddressComponent,
  AdminOpportunityRequest,
  AllocationContractorResponse,
  Association,
  OpportunityResponse,
  OpportunityStatusRequest,
  PaymentMethodResponse,
  ServicesResponse,
} from '~/types';
import LocationStep from './components/LocationStep';
import SummaryCard from './components/SummaryCard';
import { Section, SectionTitle } from './styles';

const INITIAL_ADDRESS: AddressComponent = {
  neighborhood: '',
  city: '',
  fullAddress: '',
};

function getLocationOrderId(locationHeader?: string) {
  if (!locationHeader) {
    return undefined;
  }

  return locationHeader.split('/').pop();
}

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

async function findOrderById(orderId: string, status: OpportunityStatusRequest): Promise<OpportunityResponse | undefined> {
  try {
    const response = await loadOpportunities(status, null, null, undefined);
    const list: OpportunityResponse[] = response?.data ?? [];
    return list.find(opp => opp.id === orderId);
  } catch (_) {
    return undefined;
  }
}

async function waitForOrderStatus(
  orderId: string,
  status: OpportunityStatusRequest,
  { tries = 8, intervalMs = 1500 } = {},
): Promise<boolean> {
  for (let attempt = 0; attempt < tries; attempt += 1) {
    const found = await findOrderById(orderId, status);
    if (found) {
      return true;
    }
    await sleep(intervalMs);
  }
  return false;
}

export default function AdminOpportunity() {
  const [association, setAssociation] = useState<Association | null>(null);
  const [customerDocument, setCustomerDocument] = useState('');
  const [service, setService] = useState<ServicesResponse | null>(null);
  const [contractor, setContractor] = useState<AllocationContractorResponse | null>(null);
  const [vehicleId, setVehicleId] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [originCoordinates, setOriginCoordinates] = useState<google.maps.LatLngLiteral>();
  const [destinationCoordinates, setDestinationCoordinates] = useState<google.maps.LatLngLiteral>();
  const [originAddress, setOriginAddress] = useState<AddressComponent>(INITIAL_ADDRESS);
  const [destinationAddress, setDestinationAddress] = useState<AddressComponent>(INITIAL_ADDRESS);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const { addToast } = useToast();
  const history = useHistory();

  const { associations } = useAssociations();
  const {
    data: allocationContractors,
    loading: loadingContractors,
    load: loadAllocationContractorsHook,
    clear: clearAllocationContractors,
  } = useAllocationContractors();
  const { data: customer, loading: loadingCustomer } = useCustomer(customerDocument.replace(/\D+/g, ''));
  const { customerVehicles, loadingVehicles } = useCustomerVehicles(customer?.id);
  const { services, loadingServices } = useServices();
  const { payments, createOrder, getTimeZoneByGoogle, mapCalculator } = useNewOrder();

  const selectedVehicle = useMemo(() => customerVehicles.find(item => item.id === vehicleId), [customerVehicles, vehicleId]);
  const selectedPayment = useMemo(
    () => payments?.find(paymentItem => paymentItem.id === paymentId),
    [paymentId, payments],
  );

  useEffect(() => {
    if (service?.id && selectedVehicle?.vehicle_customer_type_description) {
      loadAllocationContractorsHook(service.id, selectedVehicle.vehicle_customer_type_description);
      setContractor(null);
    } else {
      clearAllocationContractors();
      setContractor(null);
    }
  }, [
    service?.id,
    selectedVehicle?.vehicle_customer_type_description,
    loadAllocationContractorsHook,
    clearAllocationContractors,
  ]);

  const canSubmit = useMemo(() => {
    const hasMandatoryFields =
      !!association &&
      !!customer?.id &&
      !!selectedVehicle &&
      !!service &&
      !!contractor &&
      !!selectedPayment &&
      !!originCoordinates;

    if (!hasMandatoryFields) {
      return false;
    }

    if (service?.displace && !destinationCoordinates) {
      return false;
    }

    return true;
  }, [
    association,
    contractor,
    customer?.id,
    destinationCoordinates,
    originCoordinates,
    selectedPayment,
    selectedVehicle,
    service,
  ]);

  const clearLocationDestination = () => {
    setDestinationCoordinates(undefined);
    setDestinationAddress(INITIAL_ADDRESS);
  };

  const handleServiceChange = (newService: ServicesResponse | null) => {
    setService(newService);

    if (!newService?.displace) {
      clearLocationDestination();
    }
  };

  const validateData = () => {
    if (!association) {
      addToast({ title: 'Validacao', type: 'error', description: 'Selecione a empresa.' });
      return false;
    }

    if (!customer?.id) {
      addToast({ title: 'Validacao', type: 'error', description: 'Cliente nao encontrado.' });
      return false;
    }

    if (!selectedVehicle) {
      addToast({ title: 'Validacao', type: 'error', description: 'Selecione um veiculo.' });
      return false;
    }

    if (!service) {
      addToast({ title: 'Validacao', type: 'error', description: 'Selecione um servico.' });
      return false;
    }

    if (!contractor) {
      addToast({ title: 'Validacao', type: 'error', description: 'Selecione um prestador.' });
      return false;
    }

    if (!selectedPayment) {
      addToast({ title: 'Validacao', type: 'error', description: 'Selecione uma forma de pagamento.' });
      return false;
    }

    if (!originCoordinates) {
      addToast({ title: 'Validacao', type: 'error', description: 'Informe a localizacao de origem.' });
      return false;
    }

    if (service.displace && !destinationCoordinates) {
      addToast({ title: 'Validacao', type: 'error', description: 'Informe a localizacao de destino.' });
      return false;
    }

    return true;
  };

  const resetForm = () => {
    setAssociation(null);
    setCustomerDocument('');
    setService(null);
    setContractor(null);
    setVehicleId('');
    setPaymentId('');
    setOriginCoordinates(undefined);
    setDestinationCoordinates(undefined);
    setOriginAddress(INITIAL_ADDRESS);
    setDestinationAddress(INITIAL_ADDRESS);
  };

  const handleSubmit = async () => {
    if (!validateData()) {
      return;
    }

    setLoadingSubmit(true);

    try {
      const timezone =
        (await getTimeZoneByGoogle({
          latitude: originCoordinates!.lat,
          longitude: originCoordinates!.lng,
        })) || 'America/Sao_Paulo';

      const originPoint = [{ lat: originCoordinates!.lat, lng: originCoordinates!.lng }];

      const calcResponse = await mapCalculator({
        time_zone_id: timezone,
        displace: service!.displace,
        service_id: service!.id,
        association_id: association!.id,
        vehicle_id: selectedVehicle!.id,
        social_security: customer!.id,
        has_picktow: false,
        points: originPoint as any,
        source: originAddress.city,
        ...(service?.displace && destinationCoordinates
          ? { destination: destinationAddress.city }
          : {}),
      });

      const coordinates: AdminOpportunityRequest['coordinates'] = [
        {
          position: 'SOURCE',
          ordination: 0,
          latitude: originCoordinates!.lat,
          longitude: originCoordinates!.lng,
          address: originAddress.fullAddress,
          neighborhood: originAddress.neighborhood,
          city: originAddress.city,
        },
      ];

      if (service?.displace && destinationCoordinates) {
        coordinates.push({
          position: 'DESTINATION',
          ordination: 1,
          latitude: destinationCoordinates.lat,
          longitude: destinationCoordinates.lng,
          address: destinationAddress.fullAddress,
          neighborhood: destinationAddress.neighborhood,
          city: destinationAddress.city,
        });
      }

      const orderPayload: AdminOpportunityRequest = {
        from_backoffice: true,
        payment: {
          token: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
          type: selectedPayment!.payment_method,
        },
        cost: calcResponse.data,
        association_name: association!.trading_name || association!.company_name,
        detached: false,
        customer_id: customer!.id,
        association_id: association!.id,
        time_zone: timezone,
        service_type: service!.description,
        vehicle: {
          type: selectedVehicle!.vehicle_customer_type_description,
          model: selectedVehicle!.model,
          model_year: selectedVehicle!.model_year,
          color: selectedVehicle!.color,
          manufacture: selectedVehicle!.manufacture,
          status: selectedVehicle!.status,
          vin: selectedVehicle!.vin,
          license_plate: selectedVehicle!.license_plate,
          state: selectedVehicle!.state,
          city: selectedVehicle!.city,
        },
        coordinates,
        association_code: association!.code,
        service_id: service!.id,
        quiz_answers: JSON.stringify([]),
        disable_auto_allocation: true,
      };

      const orderResponse = await createOrder(orderPayload);
      const createdOrderId =
        getLocationOrderId(orderResponse.headers?.location) ||
        orderResponse.data?.id ||
        orderResponse.data?.order_id;

      console.log('[AdminOpportunity] Ordem criada:', createdOrderId);

      if (!createdOrderId) {
        throw new Error('Nao foi possivel identificar a oportunidade criada.');
      }

      let reachedManual = await waitForOrderStatus(
        createdOrderId,
        OpportunityStatusRequest.MANUAL_ALLOCATION,
        { tries: 10, intervalMs: 2000 },
      );
      console.log('[AdminOpportunity] Chegou em MANUAL_ALLOCATION direto?', reachedManual);

      if (!reachedManual) {
        const reachedAllocating = await waitForOrderStatus(
          createdOrderId,
          OpportunityStatusRequest.ALLOCATING,
          { tries: 10, intervalMs: 2000 },
        );
        console.log('[AdminOpportunity] Chegou em ALLOCATING?', reachedAllocating);

        if (reachedAllocating) {
          try {
            await sendToManualAllocationAPI(createdOrderId, {
              reallocate: false,
              cancellation_reason: 'BACKOFFICE - Geração admin para alocação manual',
            });
            console.log('[AdminOpportunity] sendToManualAllocation OK');
          } catch (sendErr: any) {
            console.error(
              '[AdminOpportunity] Falha em sendToManualAllocation:',
              sendErr?.response?.data ?? sendErr,
            );
          }

          reachedManual = await waitForOrderStatus(
            createdOrderId,
            OpportunityStatusRequest.MANUAL_ALLOCATION,
            { tries: 10, intervalMs: 2000 },
          );
          console.log('[AdminOpportunity] Chegou em MANUAL_ALLOCATION pós send?', reachedManual);
        }
      }

      if (!reachedManual) {
        addToast({
          title: 'Atenção',
          type: 'info',
          description: 'Oportunidade está em alocação manual mas a alocação automática para o prestador falhou. Aloque manualmente.',
        });
        resetForm();
        history.push('/opportunity-manager#MANUAL_ALLOCATION');
        return;
      }

      try {
        console.log('[AdminOpportunity] Alocando para contractor:', contractor!.id);
        await allocateOpportunityForContractor(createdOrderId, contractor!.id);
        addToast({
          title: 'Sucesso',
          type: 'success',
          description: 'Oportunidade criada e alocada para o prestador selecionado.',
        });
      } catch (allocError: any) {
        console.error(
          '[AdminOpportunity] Falha ao alocar contractor (ordem ja esta em MANUAL_ALLOCATION):',
          allocError?.response?.data ?? allocError,
        );
        const apiMessages: string[] = allocError?.response?.data?.messages ?? [];
        const detail = apiMessages.join(', ') || allocError?.message || 'erro desconhecido';
        addToast({
          title: 'Atenção',
          type: 'info',
          description: `Oportunidade criada e enviada para alocação manual, mas a alocação automática para o prestador falhou (${detail}). Aloque pela aba "Alocação Manual".`,
        });
      }

      resetForm();
      history.push('/opportunity-manager#MANUAL_ALLOCATION');
    } catch (err: any) {
      console.error('[AdminOpportunity] Erro ao gerar oportunidade:', err?.response?.data ?? err);
      const apiMessages: string[] = err?.response?.data?.messages ?? [];
      const knownMessages: Record<string, string> = {
        'message.orders.in-progress': 'Este cliente já possui uma ordem em andamento.',
        'message.order.state-violation': 'A ordem está em um estado inválido para alocação.',
      };
      const mapped = apiMessages.map(m => knownMessages[m] ?? m).join(' ');
      addToast({
        title: 'Erro',
        type: 'error',
        description: mapped || err?.response?.data?.message || 'Nao foi possivel gerar a oportunidade para o prestador.',
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <>
      <Title marginBottom={20}>Gerar Oportunidade (Admin)</Title>

      <Section>
        <SectionTitle>Empresa e Cliente</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CustomAutocomplete
              id='admin-opportunity-association'
              options={associations}
              getOptionLabel={item => item.trading_name || item.company_name || item.code || item.id}
              renderInput={params => <TextField {...params} label='Empresa' variant='outlined' size='small' />}
              onChange={(_, value) => setAssociation(value)}
              value={association}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label='CPF/CNPJ do cliente'
              variant='outlined'
              size='small'
              value={customerDocument}
              onChange={({ target }) => setCustomerDocument(target.value)}
              helperText={customer ? `Cliente: ${customer.name}` : loadingCustomer ? 'Buscando cliente...' : ''}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label='Veiculo do cliente'
              variant='outlined'
              size='small'
              value={vehicleId}
              onChange={({ target }) => setVehicleId(target.value)}
              select
              disabled={!customer || loadingVehicles}
              fullWidth
            >
              {customerVehicles.map(vehicle => (
                <MenuItem key={vehicle.id} value={vehicle.id}>
                  {vehicle.license_plate} - {vehicle.model} ({vehicle.model_year || 'N/A'})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Section>

      <Section>
        <SectionTitle>Servico e Prestador</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CustomAutocomplete
              id='admin-opportunity-service'
              options={services || []}
              getOptionLabel={item => item.description || item.id}
              renderInput={params => <TextField {...params} label='Servico' variant='outlined' size='small' />}
              onChange={(_, value) => handleServiceChange(value)}
              value={service}
              loading={loadingServices}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomAutocomplete
              id='admin-opportunity-contractor'
              options={allocationContractors}
              getOptionLabel={item => item.name || item.company_name || item.id}
              renderInput={params => (
                <TextField
                  {...params}
                  label='Prestador'
                  variant='outlined'
                  size='small'
                  helperText={
                    !service || !selectedVehicle
                      ? 'Selecione serviço e veículo para listar prestadores elegíveis'
                      : ''
                  }
                />
              )}
              onChange={(_, value) => setContractor(value)}
              value={contractor}
              loading={loadingContractors}
              disabled={!service || !selectedVehicle}
            />
          </Grid>
        </Grid>
      </Section>

      <Section>
        <SectionTitle>Localizacao</SectionTitle>
        <LocationStep
          requireDestination={!!service?.displace}
          onOriginCoordinatesChange={setOriginCoordinates}
          onDestinationCoordinatesChange={setDestinationCoordinates}
          onOriginAddressChange={setOriginAddress}
          onDestinationAddressChange={setDestinationAddress}
        />
      </Section>

      <Section>
        <SectionTitle>Pagamento</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label='Forma de pagamento'
              variant='outlined'
              size='small'
              value={paymentId}
              onChange={({ target }) => setPaymentId(target.value)}
              select
              fullWidth
            >
              {(payments || []).map((paymentMethod: PaymentMethodResponse) => (
                <MenuItem key={paymentMethod.id} value={paymentMethod.id}>
                  {paymentMethod.description || paymentMethod.payment_method}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Section>

      <Section>
        <SummaryCard
          association={association?.trading_name || association?.company_name || ''}
          customer={customer?.name || ''}
          vehicle={
            selectedVehicle
              ? `${selectedVehicle.license_plate} - ${selectedVehicle.model} (${selectedVehicle.model_year || 'N/A'})`
              : ''
          }
          service={service?.description || ''}
          contractor={contractor?.name || ''}
          paymentMethod={selectedPayment?.description || selectedPayment?.payment_method || ''}
          origin={originAddress.fullAddress || ''}
          destination={service?.displace ? destinationAddress.fullAddress || '' : 'Nao se aplica'}
          loading={loadingSubmit}
          onSubmit={handleSubmit}
        />
      </Section>
    </>
  );
}
